import { InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  optional?: boolean;
  helperText?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, optional, className = '', fullWidth = false, type, value, ...props }, ref) => {
    // Hide number input arrows
    const hideArrows = type === 'number' ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' : '';
    
    // When using react-hook-form's register, value comes from props.value (via register)
    // If value is explicitly passed as prop, use it (controlled). Otherwise let register handle it
    // Ensure value is never null - default to empty string for controlled inputs
    const inputProps = value !== undefined 
      ? { ...props, value: value ?? '' }
      : props;
    
    return (
      <div className={`space-y-1.5 ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <div className="flex items-center justify-between">
            <label
              htmlFor={props.id}
              className="text-sm font-medium text-slate-700"
            >
              {label}
            </label>
            {optional && <span className="text-xs text-slate-400">Optional</span>}
          </div>
        )}
        <input
          ref={ref}
          type={type}
          className={`h-9 px-3 text-sm text-slate-900 border border-slate-200 rounded-lg 
            focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 
            disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed
            transition-shadow ${hideArrows} ${fullWidth ? 'w-full' : ''} ${className}`}
          {...inputProps}
        />
        {helperText && !error && (
          <p className="text-xs text-slate-500">{helperText}</p>
        )}
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
